export type PhoneNumberHash = string;
export type EmailHash = string;

export type PikkoloCartContents = {
  nrOfRefrigeratedItems: number;
  nrOfFrozenItems: number;
};

export type PikkoloAddress = {
  postalCode: string;
  street: string;
  city: string;
  country: string;
};

export type PikkoloDeliveryTime = {
  id: string;
  date: string;
  available: boolean;
};

export type PikkoloStation = {
  id: string;
  name: string;
  address: PikkoloAddress;
  imageUrl: string;
  latitude: number;
  longitude: number;
  nextAvailableDeliveryTime: PikkoloDeliveryTime | undefined;
  deliveryTimes: PikkoloDeliveryTime[];
};

export type PikkoloStationAndTime = {
  station: PikkoloStation;
  deliveryTime: PikkoloDeliveryTime;
};

export type PikkoloAvailableStations = {
  bestAvailableStation: PikkoloStation | undefined;
  stations: PikkoloStation[];
};

export type PikkoloEnvironment = "production" | "staging" | "development";

export type PikkoloGetAvailableStationParams = {
  cartContents: PikkoloCartContents;
  countryCode: string;
  city: string;
  postalCode: string;
  street: string;
  phoneNumberHash: PhoneNumberHash;
  emailHash: EmailHash;
  startDate?: Date;
  endDate?: Date;
};

function createQueryString(params: Record<string, any>) {
  let queryString = "";
  for (let key in params) {
    let value = params[key];
    if (value !== undefined && value !== null) {
      queryString += `${key}=${encodeURIComponent(value)}&`;
    }
  }
  return queryString;
}

export enum PikkoloErrorMode {
  ThrowError,
  LogError,
}

export class PikkoloSDK {
  fetch: (
    input: RequestInfo,
    init?: RequestInit | undefined
  ) => Promise<Response>;

  apiUrl: string;
  modalUrl: string;
  errorMode: PikkoloErrorMode = PikkoloErrorMode.ThrowError;

  vendorId: string;

  stations: PikkoloStation[];
  bestAvailableStation: PikkoloStation | undefined;
  selectedStationAndDeliveryTime: PikkoloStationAndTime | undefined;

  preloadModalPromise:
    | Promise<
        { containerDiv: HTMLDivElement; iframe: HTMLIFrameElement } | undefined
      >
    | undefined;

  public static make(environment: PikkoloEnvironment, vendorId: string) {
    let hostName;
    if (environment == "production") {
      hostName = "api.pikkolo.is";
    } else if (environment == "staging") {
      hostName = "api-staging.pikkolo.is";
    } else {
      hostName = "api-dev.pikkolo.is";
    }

    let apiUrl = `https://${hostName}/api/public/v1`;
    let modalUrl = `https://${hostName}/sdk/choose-station-modal`;

    return new PikkoloSDK(
      (input, init) => fetch(input, init),
      apiUrl,
      modalUrl,
      vendorId,
      environment === "production"
        ? PikkoloErrorMode.LogError
        : PikkoloErrorMode.ThrowError
    );
  }

  constructor(
    fetch: (
      input: RequestInfo,
      init?: RequestInit | undefined
    ) => Promise<Response>,
    apiUrl: string,
    modalUrl: string,
    vendorId: string,
    errorMode?: PikkoloErrorMode
  ) {
    this.fetch = fetch;
    this.apiUrl = apiUrl;
    this.modalUrl = modalUrl;
    this.vendorId = vendorId;
    this.errorMode = errorMode || PikkoloErrorMode.ThrowError;
    this.stations = [];

    const sdk = this;
    if (window && window.addEventListener) {
      window.addEventListener("load", function () {
        sdk.preloadModalPromise = sdk.preloadChooseAnotherStationModal();
      });
    }
  }

  /**
   * Returns a hash code from a string
   * @param  {String} str The string to hash.
   * @return {String}    A weak hash code that shouldn't be used for cryptographic purpose.
   * @see http://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
   */
  static hashCode(str: string): string {
    let hash = 0;
    for (let i = 0, len = str.length; i < len; i++) {
      let chr = str.charCodeAt(i);
      hash = (hash << 5) - hash + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return btoa(hash.toString(16));
  }

  /**
   * Anonymizes a phone number so it can be shared with the Pikkoló SDK
   * @param phoneNumber The phone number to anonymize
   * @returns The anonymized phone number
   */
  static hashPhoneNumber(phoneNumber: string): PhoneNumberHash {
   return PikkoloSDK.hashCode(phoneNumber.replace(/ /g, "")).toString();
  }

  /**
   * Anonymizes an email so it can be shared with the Pikkoló SDK
   * @param email The email address of the user to anonymize
   * @returns The anonymized email address
   */
  static hashEmail(email: string): EmailHash {
    return PikkoloSDK.hashCode(email.replace(/ /g, "")).toString();
  }

  private validateParameters(params: PikkoloGetAvailableStationParams) {
    if (!params.cartContents) {
      throw new Error("[Pikkolo] parameter `cartContents` is required");
    } else if (params.cartContents.nrOfRefrigeratedItems < 0) {
      throw new Error(
        "[Pikkolo] parameter `cartContents.nrOfRefrigeratedItems` has to be >= 0"
      );
    } else if (params.cartContents.nrOfFrozenItems < 0) {
      throw new Error(
        "[Pikkolo] parameter `cartContents.nrOfFrozenItems` has to be >= 0"
      );
    } else if (
      params.cartContents.nrOfRefrigeratedItems == 0 &&
      params.cartContents.nrOfFrozenItems == 0
    ) {
      throw new Error(
        "[Pikkolo] parameter `cartContents.nrOfRefrigeratedItems` and `cartContents.nrOfFrozenItems` cannot both be 0"
      );
    }

    if (!params.countryCode) {
      throw new Error("[Pikkolo] parameter `countryCode` is required");
    } else if (params.countryCode.length != 2) {
      throw new Error(
        "[Pikkolo] parameter `countryCode` has to be a 2-letter country code"
      );
    }

    if (!params.city) {
      throw new Error("[Pikkolo] parameter `city` is required");
    }

    if (!params.postalCode) {
      throw new Error("[Pikkolo] parameter `postalCode` is required");
    }

    if (params.startDate && !(params.startDate instanceof Date)) {
      throw new Error("[Pikkolo] parameter `startDate` has to be a Date");
    }

    if (params.endDate && !(params.endDate instanceof Date)) {
      throw new Error("[Pikkolo] parameter `endDate` has to be a Date");
    }

    if (
      params.startDate &&
      params.endDate &&
      params.startDate > params.endDate
    ) {
      throw new Error(
        "[Pikkolo] parameter `startDate` has to be before `endDate`"
      );
    }
  }

  private validateParametersAndReportErrors(
    params: PikkoloGetAvailableStationParams
  ) {
    if (this.errorMode === PikkoloErrorMode.ThrowError) {
      this.validateParameters(params);
    } else {
      try {
        this.validateParameters(params);
      } catch (e) {
        if (e instanceof Error) {
          console.error(e.message);
          return Promise.resolve({
            bestAvailableStation: undefined,
            stations: [],
          });
        } else {
          console.error("[Pikkolo] Unknown error");
          return Promise.resolve({
            bestAvailableStation: undefined,
            stations: [],
          });
        }
      }
    }
  }

  /**
   * Asynchronously fetches a list of all available Pikkoló stations, sorted by proximity and preference.
   * @param params.cartContents The number of refrigerated and frozen items in the user cart.
   *                      Used to check availability of slots in refrigerated and freezer slots in the Pikkoló stations.
   * @param params.countryCode The country code of the user. 2-letter ISO 3166-1 alpha-2 country code.
   * @param params.city The city of the user.
   * @param params.postalCode The postal code of the user.
   * @param params.street Optional: The street of the user.
   * @param params.phoneNumberHash The anonymized phone number of the user.
   * @param params.emailHash The anonymized email address of the user.
   * @param params.startDate Optional: The start date of the delivery window.
   * @param params.endDate Optional: The end date of the delivery window.
   * @returns A promise that resolves to a list of Pikkolo stations, sorted by proximity and preference
   */
  getAvailableStations(
    params: PikkoloGetAvailableStationParams
  ): Promise<PikkoloAvailableStations> {
    this.validateParametersAndReportErrors(params);

    try {
      let sdk = this;
      let url =
        this.apiUrl +
        "/stations/available?" +
        createQueryString({
          nrOfRefrigeratedItems: params.cartContents.nrOfRefrigeratedItems,
          nrOfFrozenItems: params.cartContents.nrOfFrozenItems,
          countryCode: params.countryCode,
          city: params.city,
          postalCode: params.postalCode,
          street: params.street,
          phoneNumberHash: params.phoneNumberHash,
          emailHash: params.emailHash,
          startDate: params.startDate
            ? params.startDate.toISOString().split("T")[0]
            : null,
          endDate: params.endDate
            ? params.endDate.toISOString().split("T")[0]
            : null,
        });
      return this.fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "default",
        headers: {
          "content-type": "application/json",
          "x-vendor-id": this.vendorId,
        },
        redirect: "follow",
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            throw new Error(response.statusText);
          }
        })
        .then((response) => {
          if (response.data && "stations" in response.data) {
            const data = response.data;
            sdk.bestAvailableStation = data.bestAvailableStation || undefined;
            sdk.stations = data.stations;
            return data;
          } else {
            throw new Error("Malformed response");
          }
        })
        .catch((error) => {
          console.error("[Pikkolo] Error when communicating with API:", error);
          return {
            bestAvailableStation: undefined,
            stations: [],
          };
        });
    } catch (error) {
      console.error("[Pikkolo] Error when communicating with API:", error);
      return Promise.resolve({
        bestAvailableStation: undefined,
        stations: [],
      });
    }
  }

  // To provide a snappy UX we preload the choose station modal in the background by appending a hidden iframe to the body,
  // wrapped in a div with a width and height set to 0
  // When the use wants to show the modal we can then simply set the width and height to 100%.
  private preloadChooseAnotherStationModal() {
    return new Promise<{
      containerDiv: HTMLDivElement;
      iframe: HTMLIFrameElement;
    }>((resolve, reject) => {
      if (document && document.body) {
        const containerDiv = document.createElement("div");
        containerDiv.style.position = "fixed";
        containerDiv.style.top = "0px";
        containerDiv.style.left = "0px";
        containerDiv.style.width = "0";
        containerDiv.style.height = "0";

        const iframe = document.createElement("iframe");
        iframe.setAttribute("src", this.modalUrl);
        iframe.style.width = "100%";
        iframe.style.height = "100%";
        iframe.style.border = "none";

        let modalIsReady = false;
        let listener: (this: Window, ev: MessageEvent<any>) => any;
        listener = (e) => {
          if (e.data.type == "PikkoloModalReady") {
            modalIsReady = true;

            window.removeEventListener("message", listener);

            if (containerDiv && iframe) {
              resolve({ containerDiv, iframe });
            } else {
              reject(
                "Unable to preload modal because containerDiv or iframe are undefined"
              );
            }
          }
        };

        window.addEventListener("message", listener);

        setTimeout(() => {
          if (!modalIsReady) {
            window.removeEventListener("message", listener);

            // Skoða retry

            reject("Modal timeout");
          }
        }, 5000);

        document.body.appendChild(containerDiv);
        containerDiv.appendChild(iframe);
      } else {
        reject(
          "Unable to preload modal because no document object found - Are you not running in a browser environment?"
        );
      }
    }).catch((error) => {
      console.error("[Pikkolo] Error when preloading modal:", error);
      return undefined;
    });
  }

  /**
   * Shows a modal that allows the user to pick another Pikkoló station
   * @returns A promise that resolves to the selected Pikkolo station and desired time
   */
  showChooseAnotherStationModal(): Promise<PikkoloStationAndTime | undefined> {
    let sdk = this;
    if (!window) {
      console.error(
        "[Pikkolo] No window object found - Are you not running in a browser environment?"
      );
      return Promise.resolve(undefined);
    }

    if (!this.preloadModalPromise) {
      this.preloadModalPromise = this.preloadChooseAnotherStationModal();
    }

    return this.preloadModalPromise
      .then((response) => {
        if (response && response.containerDiv && response.iframe) {
          const { containerDiv, iframe } = response;
          return new Promise<PikkoloStationAndTime | undefined>(
            (resolve, reject) => {
              let listener: (this: Window, ev: MessageEvent<any>) => any;
              let cleanUp = () => {
                containerDiv.style.width = "0";
                containerDiv.style.height = "0";

                window.removeEventListener("message", listener);
              };

              listener = (e) => {
                if (e.data.type == "PikkoloModalClose") {
                  sdk.selectedStationAndDeliveryTime = e.data.selectedStation;

                  cleanUp();
                  resolve(e.data.selectedStation);
                }
              };

              containerDiv.style.width = "100%";
              containerDiv.style.height = "100%";

              window.addEventListener("message", listener);

              if (iframe.contentWindow) {
                iframe.contentWindow.postMessage(
                  {
                    type: "PikkoloModalInit",
                    selectedStation:
                      sdk.selectedStationAndDeliveryTime ||
                      (sdk.bestAvailableStation && {
                        station: sdk.bestAvailableStation,
                        deliveryTime:
                          sdk.bestAvailableStation.nextAvailableDeliveryTime,
                      }),
                    stations: sdk.stations,
                  },
                  "*"
                );
              } else {
                reject(
                  "[Pikkolo] Unable to show modal because iframe contentWindow is undefined"
                );
              }
            }
          );
        } else {
          return Promise.reject(
            "[Pikkolo] Unable to preload modal because response is undefined"
          );
        }
      })
      .catch((error) => {
        console.error("[Pikkolo] Error when preloading modal:", error);
        return undefined;
      });
  }
}
